import { Box, Chip } from '@customink/pigment-react';
import {
  getSizeLabel,
  getSizingGroups,
  usePricing,
  useProduct,
} from '@customink/project-service-react-components';
import type { ProjectItem } from '@customink/project-service-react-components';
import type { CSSProperties } from 'react';

import type { ModalState } from '@/atoms/editSizesModalAtoms';

export default function SizeChips({
  onChipClick,
  item,
}: {
  onChipClick?: ((modalState: ModalState) => void) | undefined;
  item: ProjectItem;
}) {
  const { isLoading: isLoadingPricing } = usePricing();
  const { data: product } = useProduct({ colorId: item.product?.colorId });
  const style: CSSProperties = {
    borderRadius: '4px',
    height: 'unset',
    padding: '2px 0',
    fontSize: '14px',
    lineHeight: '16px',
  };

  const handleChipClick = (size: string) =>
    onChipClick?.({
      designCid: item?.design?.cid || '',
      size: getSizeLabel(size),
      projectItemId: item.id,
      source: 'edit_sizes_link',
    });

  // Get all available sizes from the item's sizesQuantities
  const availableSizes = (item.sizesQuantities || [])
    .filter(({ size, quantity }) => !!size && quantity && quantity > 0)
    .map(({ size }) => size as string);

  // match the order of available sizes with the same order as product.sizes
  const orderedAvailableSizes =
    product?.sizes?.filter((size) => availableSizes.includes(size)) || [];

  // Group the sizes using getSizingGroups
  const sizeGroups = getSizingGroups(orderedAvailableSizes);

  // Flatten the groups in the correct order (Youth, Adult, One Size)
  // Modern JS Object.entries itterates in order of key insertion
  // key insertion order is youth, adult, oneSize from sizes.ts
  // TODO: Codesmell? this doesn't seem like an obvious ordering
  const orderedSizes = Object.entries(sizeGroups)
    .filter(([_label, sizes]) => sizes.length)
    .flatMap(([_label, sizes]) => sizes);

  return (
    <Box display="flex" flexWrap="wrap" gap="4px" margin="1px 0 1px -1px">
      {orderedSizes.map((size) => {
        const sizeQuantity = item.sizesQuantities?.find(
          (sq) => sq.size === size,
        );
        if (!sizeQuantity?.quantity) return null;

        const label = `${getSizeLabel(size)} ${sizeQuantity.quantity}`;
        return (
          <Chip
            disabled={isLoadingPricing}
            key={size}
            label={label}
            onClick={() => handleChipClick(size)}
            size="small"
            sx={style}
          />
        );
      })}
    </Box>
  );
}
